import { WalkPathsRenderer } from "@/components/r3f/renderers/odometry-paths/walk-paths-renderer";
import { selectActivePath } from "@/modes/walk-mode/walk-scene";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementGenericImgSheet,
  IElementImg360,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { JSX } from "react";
import { Vector3 } from "three";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";

type FlyWalkPathsRendererProps = {
  /** Paths to be rendered in the current scene */
  paths: IElementGenericOdometryPath[];
  /** The currently active pano if any */
  pano: IElementImg360 | undefined;
  /** Sheet to compute path elevations */
  sheetForElevation: IElementGenericImgSheet | undefined;
  /** Whether the rendered paths should fade off */
  fadeOff: boolean;
  /** Callback issued when a pano is clicked */
  onPanoClicked(pano: IElementImg360): void;
  /** Callback issued when a waypoint without a pano is clicked */
  onWalkToPosition?(position: Vector3): void;
};

/** @returns a component to render paths in flywalk mode */
export function FlyWalkPathsRenderer({
  paths,
  pano,
  sheetForElevation,
  fadeOff,
  onPanoClicked,
  onWalkToPosition,
}: FlyWalkPathsRendererProps): JSX.Element | null {
  const { clippingPlanes, activeToolAndPicking } = useToolsAcquaintance();

  const activePath = useAppSelector(selectActivePath(paths, pano));

  if (activeToolAndPicking.isPickingToolActive || paths.length === 0) {
    return null;
  }

  return (
    <WalkPathsRenderer
      fadeOff={fadeOff}
      paths={paths}
      activePath={activePath}
      activePano={pano}
      sheetForElevation={sheetForElevation}
      clippingPlanes={clippingPlanes}
      alignmentRequired={!!pano}
      setActiveElement={onPanoClicked}
      onWalkToPosition={onWalkToPosition}
    />
  );
}
